/**
 * 🚀 PIXIE BUBBLE - La commandante qui chuchote des conseils
 * 
 * Apparaît pour :
 * - Donner des icebreakers
 * - Conseiller pendant les conversations
 * - Encourager quand ça match
 * - Réconforter quand c'est dur
 * 
 * Design : Bulle magique avec particules et glow effect
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Wand2, Heart, Lightbulb } from 'lucide-react';
import { useHeartbeats, PIXIE } from '../../core/HeartbeatsProvider';
import { PixieAvatar } from '../../PixieAvatar';

export function PixieBubble({ position = 'bottom-right' }) {
  const { pixieMessage, pixieVisible, setPixieVisible } = useHeartbeats();
  const [particles, setParticles] = useState([]);
  
  // ✨ Générer des particules magiques
  useEffect(() => {
    if (pixieVisible) {
      const newParticles = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        delay: Math.random() * 0.5,
        duration: 1 + Math.random() * 2
      }));
      setParticles(newParticles);
    }
  }, [pixieVisible]);
  
  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
  };
  
  return (
    <AnimatePresence>
      {pixieVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          className={`fixed ${positionClasses[position]} z-50 max-w-xs`}
          onClick={(e) => e.stopPropagation()}
          data-testid="pixie-bubble"
        >
          {/* ✨ Particules magiques */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {particles.map((p) => (
              <motion.div
                key={p.id}
                className="absolute w-2 h-2 rounded-full"
                style={{
                  left: `${p.x}%`,
                  top: `${p.y}%`,
                  background: `linear-gradient(135deg, ${PIXIE.colors.primary}, ${PIXIE.colors.secondary})`
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1.5, 0],
                  y: [0, -30, -60]
                }}
                transition={{
                  duration: p.duration,
                  delay: p.delay,
                  repeat: Infinity,
                  repeatDelay: 1
                }}
              />
            ))}
          </div>
          
          {/* 💬 Bulle principale */}
          <motion.div
            className="relative rounded-2xl p-4 shadow-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.95), rgba(139, 92, 246, 0.95))',
              boxShadow: `0 0 30px ${PIXIE.colors.glow}, 0 10px 40px rgba(0,0,0,0.3)`
            }}
            animate={{
              boxShadow: [
                `0 0 30px ${PIXIE.colors.glow}, 0 10px 40px rgba(0,0,0,0.3)`,
                `0 0 50px ${PIXIE.colors.glow}, 0 10px 40px rgba(0,0,0,0.3)`,
                `0 0 30px ${PIXIE.colors.glow}, 0 10px 40px rgba(0,0,0,0.3)`
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {/* Bouton fermer */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setPixieVisible(false);
              }}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition-colors"
              data-testid="pixie-close-btn"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            
            {/* Header avec avatar Pixie */}
            <div className="flex items-center gap-2 mb-2">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <PixieAvatar size="md" withGlow />
              </motion.div>
              <div>
                <span className="text-white font-bold text-sm">{PIXIE.name}</span>
                <span className="text-white/70 text-xs block">{PIXIE.title}</span>
              </div>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              >
                <Wand2 className="w-5 h-5 text-yellow-300" />
              </motion.div>
            </div>
            
            {/* Message */}
            <p className="text-white text-sm leading-relaxed">
              {pixieMessage}
            </p>
            
            {/* Actions rapides */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setPixieVisible(false);
                }}
                className="flex-1 py-1.5 px-3 rounded-full bg-white/20 hover:bg-white/30 text-white text-xs font-medium transition-colors flex items-center justify-center gap-1"
                data-testid="pixie-thanks-btn"
              >
                <Sparkles className="w-3 h-3" />
                Merci Pixie !
              </button>
            </div>
          </motion.div>
          
          {/* 🧚‍♀️ Petit triangle pointer */}
          <div
            className="absolute -bottom-2 right-8 w-4 h-4 rotate-45"
            style={{
              background: `linear-gradient(135deg, ${PIXIE.colors.primary}, ${PIXIE.colors.secondary})`
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PixieBubble;
